import { useFormBehaviors } from 'hooks';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { METHOD_PATCH } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';

import { toastService } from 'services';
import i18next from 'i18next';
import { FieldNameType } from 'types/common.types';
import { CreateShiftCardContext } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftCard/CreateShiftCard.context';
import {
  ChangeFunctionType,
  GQLAPIContextType,
} from '@bookjane2/bookjane-design-library/lib/common.types';
import { OrderBid } from 'types/graphql';
import { AwardTeamMemberData } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.constants';
import { createGQLDataContext } from 'utils/createDataContext';
import { ADD_SHIFT_AWARD, DENY_ALL_BIDS } from 'constants/endpoints';
import {
  ShiftModalUIContext,
  shiftModalRoutingService,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import { queryRefetchService } from 'services/QueryRefetchService';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { AwardTeamMemberContext } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.data.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';

export const AwardTeamMemberViewFormContext = createGQLDataContext({});
export const AwardTeamMemberViewFormProvider: FC = ({ children }) => {
  const { shift_id: orderRelayId } = useContext(ShiftModalUIContext);
  const { data: modalData } = useShiftModalDataContext();
  const orderDisplayId = modalData?.order?.id;

  const { createShift } = useContext(CreateShiftCardContext);
  const { data } = useContext(AwardTeamMemberContext);

  const {
    values: { request_janes = '', quantity },
  } = createShift;
  const [modalValues, setModalValues] = useState({
    request_janes: request_janes,
    estimatedTotalPayout: 0,
  });
  const [dirtyFields, setDirtyFields] = useState<FieldNameType[]>([]);
  const selectedJanes = modalValues.request_janes!.split(',').filter((id) => id !== '');
  const { apiRequest } = useAPIActions();
  const { refreshBackgroundPage } = useShiftModalPropsContext();
  const handleChange = useCallback(
    (e: { target: { name: string; value: string } }) => {
      const {
        target: { value: request_janes },
      } = e;

      const _modalValues = { ...modalValues };
      setDirtyFields([...dirtyFields].concat(['request_janes']));

      if (quantity[0] === '1') {
        _modalValues['request_janes'] = request_janes;
      } else {
        let updatedSelectedJanes;
        if (selectedJanes.includes(request_janes)) {
          updatedSelectedJanes = selectedJanes.filter((id) => id !== request_janes);
        } else {
          updatedSelectedJanes = [...selectedJanes, request_janes];
        }

        _modalValues['request_janes'] = updatedSelectedJanes.join(',');
      }
      setModalValues(_modalValues);
    },
    [dirtyFields, modalValues, quantity, selectedJanes],
  );

  const handleDenyAllBids = useCallback(() => {
    return apiRequest(
      {
        __typename: 'LegacyAPI',
        method: METHOD_PATCH,
        url: DENY_ALL_BIDS(orderDisplayId!),
      },
      {
        onSuccess: () => {
          toastService.success(i18next.t('shift_modal:DENY_ALL_BIDS_AND_EXTEND_SUCCESS'));
          refreshBackgroundPage();
          queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${orderRelayId}`);
          shiftModalRoutingService.close();
        },
        onError: handleGenericErrorMessage,
      },
    );
  }, [apiRequest, orderDisplayId, orderRelayId]);

  const handleSave = useCallback(async () => {
    return await apiRequest(
      {
        __typename: 'LegacyAPI',
        method: METHOD_PATCH,
        url: ADD_SHIFT_AWARD(orderDisplayId!),
        body: {
          jane_id: selectedJanes.toString(),
        },
      },
      {
        onSuccess: async () => {
          toastService.success(i18next.t('common:SHIFT_SUCCESSFULLY_AWARDED'));
          refreshBackgroundPage();
          queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${orderRelayId}`);
          shiftModalRoutingService.close();
        },
        onError: async () => {
          shiftModalRoutingService.goToView('AwardUnavailableView');
        },
      },
    );
  }, [apiRequest, orderDisplayId, orderRelayId, selectedJanes]);
  const form = useFormBehaviors({
    isDirtyCheckEnabled: true,
    initialState: {},
    onSubmit: handleSave,
    validations: {},
  });

  const hasConflictedShifts = useMemo(() => {
    if (!data?.order?.bids || !selectedJanes?.length) {
      return false;
    }

    const result = selectedJanes.some((janeId) => {
      const bid = data.order?.bids?.find((bidItem: OrderBid) => bidItem?.jane?.id === janeId);
      const hasConflicts = bid?.conflictedShifts && bid.conflictedShifts.length > 0;

      return hasConflicts;
    });

    return result;
  }, [data?.order?.bids, selectedJanes]);

  const value = useMemo(() => {
    return {
      ...form,
      handleChange,
      selectedJanes,
      handleDenyAllBids,
      hasConflictedShifts,
    };
  }, [form, handleChange, handleDenyAllBids, selectedJanes, hasConflictedShifts]);

  return (
    <AwardTeamMemberViewFormContext.Provider value={value}>
      {children}
    </AwardTeamMemberViewFormContext.Provider>
  );
};

export const useAwardTeamMemberViewFormContext = (): GQLAPIContextType<AwardTeamMemberData> & {
  handleChange: ChangeFunctionType;
  handleDenyAllBids: () => void;
  selectedJanes: string[];
  onSubmit: (e?: { preventDefault?: () => void }) => void;
  hasConflictedShifts: boolean;
} => useContext(AwardTeamMemberViewFormContext);
